import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { map, takeUntil } from "rxjs/operators";
import { ActivatedRoute, IsActiveMatchOptions, Router } from "@angular/router";
import { ManagementNavigationService } from "management/management-navigation.service";
import { BehaviorSubject, combineLatest, Subject } from "rxjs";
import { BreadCrumbTemplate } from "common/breadcrumb/breadcrumb.component";
import { HeadlineTranslation } from "common/page/page-header.component";
import { TranslateService } from "@ngx-translate/core";
import { DropdownEntry } from "management/process-interfaces/process-interfaces-filter.component";
import { ProcessInterfacesService } from "management/process-interfaces/process-interfaces.service";
import { MainService } from "core/main.service";
import { ScreenSize } from "core/screensize";
import { Tab } from "common/tab/tab.interface";

export enum ProcessInterfacesParams {
	WORKSPACEID = "workspaceId",
	TARGETWORKSPACEID = "targetWorkspaceId",
	PVFORINTERFACES = "pvForInterfaces",
	PV = "processVersion",
	SHOWONLYCHANGES = "showOnlyChanges",
	WORKINGVERSION = "_wv",
	VALIDVERSION = "_vv",
	MERGE = "merge",
	TYPE = "type",
}

export enum InterfaceState {
	ALL = "all",
	MODIFIED = "modified",
}

@Component({
	templateUrl: "./process-interfaces.component.html",
	styleUrls: ["./process-interfaces.component.scss"],
})
export class ProcessInterfacesComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<boolean>();
	private _currentWorkspacePage: number = 1;
	private _showProcessVersionSelector: boolean = false;

	breadCrumb$ = new BehaviorSubject<BreadCrumbTemplate | undefined>(undefined);
	pageHeader$ = new BehaviorSubject<HeadlineTranslation | undefined>(undefined);

	showProcessVersionSwitch$ = new BehaviorSubject<boolean>(false);
	backButtonDisabled$ = new BehaviorSubject<boolean>(false);

	isDetailedView$ = new BehaviorSubject<boolean>(false);
	_isDetailedView = false;

	showSecondCard$ = new BehaviorSubject<boolean>(false);
	isFilterToolbarVisible: boolean = false;
	dropdownContent$ = new BehaviorSubject<DropdownEntry[]>([]);

	isFullSize: boolean = false;
	showFilterActive: boolean = false;

	tabs: Tab[] = [];
	private breadcrumpath: string;
	private processVersionToCompareWith?: string;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private managementNavigationService: ManagementNavigationService,
		private translateService: TranslateService,
		private processInterfacesService: ProcessInterfacesService,
		private changeDetector: ChangeDetectorRef,
		private mainService: MainService,
	) {
		const navigation = this.router.getCurrentNavigation();
		const state = navigation?.extras.state as { breadcrumpath: string };
		this.breadcrumpath = state?.breadcrumpath ?? "management";
	}

	ngOnInit(): void {
		this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap) => {
			this.managementNavigationService.updateNavigation(paramMap.get("workspaceId")!);
		});

		combineLatest([
			this.route.paramMap,
			this.route.queryParamMap,
			this.route.data,
			this.processInterfacesService.targetWorkspaceId$,
			this.processInterfacesService.workspacePage$,
			this.processInterfacesService.filterableElementTypes$,
			this.mainService.applicationState.screenSize,
		])
			.pipe(
				map(([paramMap, queryParamMap, data, targetWorkspaceId, workspacePage, elementTypes, screenSize]) => {
					this._showProcessVersionSelector = (data.showProcessVersionSelector ?? false) && !this.isModifiedTabActive();
					this.processVersionToCompareWith = queryParamMap.has(ProcessInterfacesParams.PVFORINTERFACES)
						? queryParamMap.get(ProcessInterfacesParams.PVFORINTERFACES)!
						: paramMap.get(ProcessInterfacesParams.PV)!;
					this.createTabs();

					this._currentWorkspacePage = workspacePage;
					this._isDetailedView = !!targetWorkspaceId;
					this.showSecondCard$.next(
						(screenSize === ScreenSize.XLARGE || screenSize === ScreenSize.LARGE) && !!targetWorkspaceId,
					);
					this.createDropdownContent(elementTypes, !!targetWorkspaceId);
					this.createPageHeader(
						paramMap.get(ProcessInterfacesParams.WORKSPACEID)!,
						paramMap.get(ProcessInterfacesParams.PV)!,
					);
					this.changeDetector.detectChanges();
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();
	}

	private createDropdownContent(elementTypes: string[], isDetailedView: boolean): void {
		const dropdownContent: DropdownEntry[] = [];
		if (elementTypes.length > 0 && isDetailedView) {
			dropdownContent.push({ ident: "all", translate: "management.process.interfaces.filter.type" });
			for (const element of elementTypes) {
				dropdownContent.push({
					ident: element,
					translate: "process.element.type.singular." + element,
				});
			}
		}
		this.dropdownContent$.next(dropdownContent);
	}

	private createPageHeader(workspaceId: string, currentProcessVersion: string): void {
		this.backButtonDisabled$.next(!this._isDetailedView);
		this.showProcessVersionSwitch$.next((this._showProcessVersionSelector ?? false) && !this._isDetailedView);

		const managementNavigationPoint = {
			labelKey: "management.navigation.title",
			route: ["//", "workspace", workspaceId, currentProcessVersion, "management"],
		};

		const processVersionNavigationPoint = {
			labelKey: "management.navigation.process.versions",
			route: ["//", "workspace", workspaceId, currentProcessVersion, "management", "versions"],
		};

		const breadCrumbTemplate = {
			links: true,
			path:
				this._showProcessVersionSelector || this.breadcrumpath === "management"
					? [managementNavigationPoint]
					: [managementNavigationPoint, processVersionNavigationPoint],
		};

		this.breadCrumb$.next(
			this._isDetailedView && workspaceId !== null && currentProcessVersion !== null ? undefined : breadCrumbTemplate,
		);
		const pageHeader = this.processVersionToCompareWith
			? {
					translate: "management.process.interfaces.page.title.withparam.version",
					params: {
						processVersion:
							this.processVersionToCompareWith === ProcessInterfacesParams.WORKINGVERSION ||
							this.processVersionToCompareWith === ProcessInterfacesParams.VALIDVERSION
								? this.translateService.instant(
										"management.process.interfaces.version." + this.processVersionToCompareWith,
								  )
								: this.processVersionToCompareWith,
					},
			  }
			: {
					translate: "management.process.interfaces.page.title",
			  };

		this.pageHeader$.next(pageHeader);
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	createTabs(): void {
		this.tabs = [];
		this.tabs.push({
			title: "management.process.versions.interfaces." + InterfaceState.ALL,
			routerLink: ["workspaces", InterfaceState.ALL, { page: 1 }],
			queryParams: { pvForInterfaces: this.processVersionToCompareWith! },
		});

		if (this.processVersionToCompareWith === ProcessInterfacesParams.WORKINGVERSION) {
			this.tabs.push({
				title: "management.process.versions.interfaces." + InterfaceState.MODIFIED,
				routerLink: ["workspaces", InterfaceState.MODIFIED, { page: 1 }],
				queryParams: { pvForInterfaces: this.processVersionToCompareWith ?? ProcessInterfacesParams.VALIDVERSION },
			});
		}
	}

	isModifiedTabActive(): boolean {
		const newURLTree = this.router.createUrlTree(["workspaces", InterfaceState.MODIFIED], { relativeTo: this.route });
		const isActiveMatchOptions: IsActiveMatchOptions = {
			matrixParams: "ignored",
			queryParams: "ignored",
			paths: "subset",
			fragment: "exact",
		};
		return this.router.isActive(newURLTree, isActiveMatchOptions);
	}

	async onDetailsClosed(): Promise<void> {
		await this.router.navigate(
			[
				"workspaces",
				this.isModifiedTabActive() ? InterfaceState.MODIFIED : InterfaceState.ALL,
				{ page: this._currentWorkspacePage },
			],
			{
				relativeTo: this.route,
				queryParamsHandling: "preserve",
			},
		);
	}
}
